/**
 * Shared "Shop form fields" — the body of the Add/Edit Shop modal.
 *
 * Used by both:
 *   - `/admin/restaurants` (full power — admin sees all fields)
 *   - `/merchant/shops` (restricted — `isAdmin=false` hides
 *     commission overrides, status picker, and referral input).
 *
 * Keeps the two consoles in lockstep: any field added here surfaces on
 * both forms automatically, eliminating drift between admin & merchant.
 */
import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { Field } from "../pages/admin/AdminRestaurants";
import ImageUploader from "./ImageUploader";
import ShopMapPicker from "./ShopMapPicker";
import { isFoodShop } from "../lib/foodCategory";
import { toggleVerticalSelection, useViewportSettled } from "../lib/verticalPicker";

export default function ShopFormFields({
    form, setForm, edit, verticals = [], isAdmin = true,
}) {
    const settled = useViewportSettled();
    const parentVerticals = useMemo(
        () => (verticals || []).filter((v) => !v.parent_id && v.is_enabled !== false && v.status !== "INACTIVE"),
        [verticals],
    );
    const subVerticalsBy = useMemo(() => {
        const m = {};
        for (const v of verticals || []) {
            if (v.parent_id && v.status !== "INACTIVE") (m[v.parent_id] = m[v.parent_id] || []).push(v);
        }
        return m;
    }, [verticals]);

    const formShopForFoodCheck = useMemo(
        () => ({ business_category_ids: form.business_category_ids || [] }),
        [form.business_category_ids],
    );
    const showFoodType = isFoodShop(formShopForFoodCheck, verticals);

    return (
        <div className="grid grid-cols-2 gap-3 text-sm">
            <Field full label="Name"><input data-testid="form-rest-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field full label="Description"><textarea className="inp" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field full label="Search keywords (comma separated)">
                <input className="inp" value={form.cuisines} onChange={(e) => setForm({ ...form, cuisines: e.target.value })} placeholder="Biryani, Mughlai · or · Plumbing, Repair" />
                <span className="text-[11px] text-muted-foreground mt-1">Helps customers find this shop in search.</span>
            </Field>
            <Field full label="Vertical / Business category">
                <div className="space-y-2.5" data-testid="vertical-picker">
                    {parentVerticals.map((v) => {
                        const pid = v.id || v.slug;
                        const subs = subVerticalsBy[pid] || [];
                        const onP = (form.business_category_ids || []).includes(pid);
                        return (
                            <div key={pid}>
                                <button
                                    type="button"
                                    data-testid={`vertical-${pid}`}
                                    onClick={() => {
                                        // Ambiguous while the keyboard is collapsing — see lib/verticalPicker.js.
                                        if (!settled()) return;
                                        setForm({ ...form, business_category_ids: toggleVerticalSelection(form.business_category_ids, pid) });
                                    }}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition border ${onP ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:border-primary"}`}
                                >{v.title || v.name}</button>
                                {subs.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5 ml-3">
                                        {subs.map((s) => {
                                            const sid = s.id || s.slug;
                                            const onS = (form.business_category_ids || []).includes(sid);
                                            return (
                                                <button
                                                    key={sid}
                                                    type="button"
                                                    data-testid={`sub-vertical-${sid}`}
                                                    onClick={() => {
                                                        if (!settled()) return;
                                                        // Selecting a subcategory auto-includes its parent
                                                        setForm({ ...form, business_category_ids: toggleVerticalSelection(form.business_category_ids, sid, { parentId: pid }) });
                                                    }}
                                                    className={`text-[11px] rounded-full px-2.5 py-1 border ${onS ? "bg-primary/15 text-primary border-primary/40" : "bg-card border-border text-muted-foreground"}`}
                                                >{s.title || s.name}</button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {parentVerticals.length === 0 && (
                        <span className="text-xs text-muted-foreground">Loading verticals…</span>
                    )}
                </div>
                <span className="text-[11px] text-muted-foreground mt-2">A shop appears under each vertical (and subcategory) you pick. Pick one or more.</span>
            </Field>
            {showFoodType && (
                <Field label="Type"><select className="inp" value={form.food_type === "OTHER" ? "VEG_NON_VEG" : form.food_type} onChange={(e) => setForm({ ...form, food_type: e.target.value })}><option>VEG</option><option>NON_VEG</option><option>VEG_NON_VEG</option></select></Field>
            )}
            <Field label="Min order ₹"><input type="number" className="inp" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} /></Field>
            <Field full label={<span className="inline-flex items-center gap-1.5"><MapPin size={13} /> Shop location on map</span>}>
                <ShopMapPicker
                    value={form.lat != null && form.lng != null ? { lat: form.lat, lng: form.lng } : null}
                    onChange={({ lat, lng, city, pincode, displayName, userInitiated }) => {
                        // iter126 — see /pages/admin/_partials/ShopForm.jsx
                        // for the full rationale. TL;DR: on user-initiated
                        // pin moves (search / drag / click) we OVERWRITE
                        // the address fields; on the initial mount reverse
                        // geocode we keep whatever the shop already had.
                        const dn = (displayName || "").split(",").map((s) => s.trim()).filter(Boolean);
                        const autoLine = dn.slice(0, 2).join(", ");
                        setForm((f) => ({
                            ...f,
                            lat,
                            lng,
                            address_city: userInitiated ? (city || "") : (f.address_city || city || ""),
                            address_pincode: userInitiated ? (pincode || "") : (f.address_pincode || pincode || ""),
                            address_line1: userInitiated ? autoLine : (f.address_line1 || autoLine || ""),
                        }));
                    }}
                />
            </Field>
            <Field label="Address line"><input className="inp" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="Building / door / street" /></Field>
            <Field label="Pincode"><input className="inp" value={form.address_pincode} onChange={(e) => setForm({ ...form, address_pincode: e.target.value })} placeholder="500034" /></Field>
            <Field full label="Cover image">
                <ImageUploader
                    value={form.cover_image_url}
                    onUploaded={(url) => setForm({ ...form, cover_image_url: url })}
                    aspect="aspect-[16/9]"
                    testId="shop-cover-image"
                />
            </Field>
            <Field full label="Shop logo">
                <ImageUploader
                    value={form.logo_url}
                    onUploaded={(url) => setForm({ ...form, logo_url: url })}
                    aspect="aspect-square"
                    testId="shop-logo"
                    className="max-w-[200px]"
                />
            </Field>
            {isAdmin && edit && (
                <Field label="Status">
                    <select className="inp" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                    </select>
                </Field>
            )}
            <Field full label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                    <input data-testid="cdc-toggle" type="checkbox" className="accent-primary w-4 h-4" checked={!!form.cdc_enabled} onChange={(e) => setForm({ ...form, cdc_enabled: e.target.checked })} />
                    <span>
                        <span className="font-semibold">Cash-During-Collection (CDC)</span>
                        <span className="block text-xs text-muted-foreground">Driver pays the restaurant the merchant payout in cash on pickup, then collects from the customer at delivery.</span>
                    </span>
                </label>
            </Field>
            <Field full label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                    <input
                        data-testid="contact-visible-toggle"
                        type="checkbox"
                        className="accent-primary w-4 h-4"
                        checked={!!form.contact_visible_to_customer}
                        onChange={(e) => setForm({ ...form, contact_visible_to_customer: e.target.checked })}
                    />
                    <span>
                        <span className="font-semibold">Show &quot;Contact merchant&quot; on customer orders</span>
                        <span className="block text-xs text-muted-foreground">When on, customers see this shop&apos;s mobile number with a Call button on the order track page.</span>
                    </span>
                </label>
            </Field>
            {!edit && (
                <Field full label="Referral code (optional)">
                    <input
                        data-testid="form-rest-referral-code"
                        className="inp font-mono uppercase tracking-wider"
                        value={form.referral_code || ""}
                        onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                        placeholder="BIZ-XXXXXX"
                    />
                    <span className="text-[11px] text-muted-foreground mt-1">
                        {isAdmin
                            ? "If a customer referred this shop, paste their code to attribute future earnings."
                            : "If a customer referred you, paste their BIZ-XXXXXX code here — they'll earn a small commission on every order you fulfil."}
                    </span>
                </Field>
            )}
            <Field full label="Services offered">
                <div className="space-y-2 mt-1">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input data-testid="hd-toggle" type="checkbox" className="accent-primary w-4 h-4"
                            checked={form.home_delivery_enabled !== false}
                            onChange={(e) => setForm({ ...form, home_delivery_enabled: e.target.checked })} />
                        <span className="font-semibold">Home Delivery</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input data-testid="pickup-toggle" type="checkbox" className="accent-primary w-4 h-4"
                            checked={form.pickup_enabled !== false}
                            onChange={(e) => setForm({ ...form, pickup_enabled: e.target.checked })} />
                        <span className="font-semibold">Store Pickup / Take Away</span>
                    </label>
                </div>
            </Field>

            <Field label="Shop phone">
                <input data-testid="form-shop-phone" type="tel" className="inp"
                    value={form.mobile_number}
                    onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                    placeholder="e.g. 9000000000" />
            </Field>
            <Field label="WhatsApp">
                <input data-testid="form-shop-whatsapp" type="tel" className="inp"
                    value={form.whatsapp_number}
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    placeholder="Defaults to shop phone if blank" />
            </Field>
            <Field full label="Email">
                <input data-testid="form-shop-email" type="email" className="inp"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="shop@example.com" />
            </Field>

            <Field label="GSTIN">
                <input data-testid="form-shop-gstin" className="inp uppercase font-mono"
                    value={form.gst_number}
                    onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                    placeholder="22AAAAA0000A1Z5" />
            </Field>
            <Field label="GST Certificate">
                <ImageUploader
                    value={form.gst_certificate_url}
                    onUploaded={(url) => setForm({ ...form, gst_certificate_url: url })}
                    aspect="aspect-[4/3]"
                    testId="shop-gst-certificate"
                    acceptPdf
                />
            </Field>

            {isAdmin && (
                <Field full label={<span className="inline-flex items-center gap-1.5">Commission override (admin)</span>}>
                    <div className="grid grid-cols-2 gap-2">
                        <input data-testid="form-comm-pct" type="number" step="0.01" className="inp"
                            value={form.commission_override_pct}
                            onChange={(e) => setForm({ ...form, commission_override_pct: e.target.value })}
                            placeholder="% (e.g. 8.5)" />
                        <input data-testid="form-comm-amount" type="number" step="0.01" className="inp"
                            value={form.commission_override_amount}
                            onChange={(e) => setForm({ ...form, commission_override_amount: e.target.value })}
                            placeholder="Flat ₹ delta (+/-)" />
                    </div>
                    <span className="text-[11px] text-muted-foreground mt-1">
                        Leave blank to inherit tenant commission. Percent replaces the rate;
                        flat amount adds (or subtracts) on top. Either or both can be set.
                    </span>
                </Field>
            )}
        </div>
    );
}

/** Empty-form scaffold shared by Admin + Merchant. */
export const EMPTY_SHOP_FORM = {
    name: "", description: "", food_type: "VEG_NON_VEG", cuisines: "",
    cover_image_url: "", logo_url: "", min_order_value: 99, status: "ACTIVE",
    cdc_enabled: false, contact_visible_to_customer: false,
    home_delivery_enabled: true, pickup_enabled: true, referral_code: "",
    business_category_ids: [], lat: null, lng: null,
    address_line1: "", address_city: "", address_pincode: "",
    gst_number: "", gst_certificate_url: "",
    mobile_number: "", whatsapp_number: "", email: "",
    commission_override_pct: "", commission_override_amount: "",
};

/** Convert a form scaffold to the payload the backend POST/PATCH expects. */
export function shopFormToPayload(form, { showFoodType = false, isAdmin = true } = {}) {
    const payload = {
        name: form.name,
        description: form.description,
        food_type: showFoodType ? form.food_type : "OTHER",
        cuisines: (form.cuisines || "").split(",").map((s) => s.trim()).filter(Boolean),
        cover_image_url: form.cover_image_url || null,
        logo_url: form.logo_url || null,
        min_order_value: Number(form.min_order_value),
        cdc_enabled: !!form.cdc_enabled,
        contact_visible_to_customer: !!form.contact_visible_to_customer,
        home_delivery_enabled: form.home_delivery_enabled !== false,
        pickup_enabled: form.pickup_enabled !== false,
        business_category_ids: form.business_category_ids || [],
        gst_number: form.gst_number || null,
        gst_certificate_url: form.gst_certificate_url || null,
        mobile_number: form.mobile_number || null,
        whatsapp_number: form.whatsapp_number || null,
        email: form.email || null,
    };
    if (isAdmin) {
        payload.status = form.status;
        payload.commission_override_pct = form.commission_override_pct === "" ? null : Number(form.commission_override_pct);
        payload.commission_override_amount = form.commission_override_amount === "" ? null : Number(form.commission_override_amount);
    }
    if (form.lat != null && form.lng != null) {
        payload.address = {
            line1: form.address_line1 || null,
            city: form.address_city || null,
            pincode: form.address_pincode || null,
            lat: form.lat,
            lng: form.lng,
        };
    }
    // iter110 — referral code must flow through both admin and merchant
    // shop-create payloads. Previously admin worked only via an inline
    // snippet in AdminRestaurants.jsx and merchant was hidden entirely;
    // centralising it here keeps both surfaces in lockstep.
    if (form.referral_code && String(form.referral_code).trim()) {
        payload.referral_code = String(form.referral_code).trim().toUpperCase();
    }
    return payload;
}

/** Hydrate a form from a restaurant row (used by edit). */
export function shopRowToForm(r) {
    const a = r.address || {};
    return {
        ...EMPTY_SHOP_FORM,
        name: r.name || "",
        description: r.description || "",
        food_type: r.food_type || "OTHER",
        cuisines: (r.cuisines || []).join(", "),
        cover_image_url: r.cover_image_url || "",
        logo_url: r.logo_url || "",
        min_order_value: r.min_order_value ?? 99,
        status: r.status || "ACTIVE",
        cdc_enabled: !!r.cdc_enabled,
        contact_visible_to_customer: !!r.contact_visible_to_customer,
        home_delivery_enabled: r.home_delivery_enabled !== false,
        pickup_enabled: r.pickup_enabled !== false,
        business_category_ids: r.business_category_ids || [],
        lat: a.lat ?? r.latitude ?? null,
        lng: a.lng ?? r.longitude ?? null,
        address_line1: a.line1 || "",
        address_city: a.city || "",
        address_pincode: a.pincode || "",
        gst_number: r.gst_number || "",
        gst_certificate_url: r.gst_certificate_url || "",
        mobile_number: r.mobile_number || "",
        whatsapp_number: r.whatsapp_number || "",
        email: r.email || "",
        commission_override_pct: r.commission_override_pct ?? "",
        commission_override_amount: r.commission_override_amount ?? "",
    };
}
